import React, { useEffect, useState } from 'react';
import classNames from 'classnames';
import { FaRegPaperPlane } from 'react-icons/fa';

import { FormattedMessage, useIntl } from '../../util/reactIntl';

import css from './ListingPage.module.css';

const COPIED_MESSAGE_MS = 2500;

/**
 * Opens the device's share sheet for the listing, or copies its link where
 * there is none (most desktop browsers).
 *
 * @component
 * @param {Object} props
 * @param {string} props.url - The listing's public URL
 * @param {string} props.title - The listing title
 * @param {string} [props.className] - Custom class for the button
 * @returns {JSX.Element} share button with a status message
 */
const ShareListingButton = props => {
  const { url, title, className } = props;
  const intl = useIntl();
  const [copied, setCopied] = useState(false);

  useEffect(() => {
    if (!copied) return undefined;
    const timeout = setTimeout(() => setCopied(false), COPIED_MESSAGE_MS);
    return () => clearTimeout(timeout);
  }, [copied]);

  const copyLink = () =>
    navigator.clipboard
      ?.writeText(url)
      .then(() => setCopied(true))
      .catch(() => setCopied(false));

  const handleClick = () => {
    if (navigator.share) {
      navigator.share({ title, url }).catch(e => {
        // AbortError means the sheet was dismissed; anything else means sharing isn't allowed here.
        if (e?.name !== 'AbortError') {
          copyLink();
        }
      });
    } else {
      copyLink();
    }
  };

  return (
    <>
      <button
        type="button"
        className={classNames(css.shareButton, className)}
        onClick={handleClick}
        aria-label={intl.formatMessage({ id: 'ListingPage.share' })}
      >
        <FaRegPaperPlane className={css.shareIcon} aria-hidden="true" />
      </button>
      <span role="status" className={copied ? css.shareStatus : css.shareStatusEmpty}>
        {copied ? <FormattedMessage id="ListingPage.shareLinkCopied" /> : null}
      </span>
    </>
  );
};

export default ShareListingButton;
